import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import organisationService from '../../services/organisationService';
import { getRoleInfo } from '../team/MemberFormModal';
import { TRACKING_CATEGORIES } from '../../constants/trackingCategories';
import '../monitoring/Monitoring.css';
import '../../components/onboarding/OrgOnboardingModal.css';
import './Settings.css';

const ORG_TYPE_LABEL = {
  developer: 'Organisation / Implementer',
  consultant: 'Consultant / Service Provider',
  regulator: 'Regulator',
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const Settings = () => {
  const { user, organisation: authOrganisation, setOrganisation: setAuthOrganisation } = useAuth();
  const [organisation, setOrganisation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingTracking, setEditingTracking] = useState(false);
  const [trackingCategories, setTrackingCategories] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [customCategoryInput, setCustomCategoryInput] = useState('');
  const [usesEiaCertificates, setUsesEiaCertificates] = useState(true);
  const [hasWorkersOnSite, setHasWorkersOnSite] = useState(true);
  const [savingTracking, setSavingTracking] = useState(false);

  const canEdit = user?.role === 'super_admin' || user?.accessLevel === 'admin';

  const fetchOrganisation = useCallback(async () => {
    try {
      setLoading(true);
      const data = await organisationService.getMine();
      setOrganisation(data.organisation);
      setNameInput(data.organisation?.name || '');
      setTrackingCategories(data.organisation?.trackingCategories || {});
      setCustomCategories(data.organisation?.customCategories || []);
      setUsesEiaCertificates(data.organisation?.usesEiaCertificates !== false);
      setHasWorkersOnSite(data.organisation?.hasWorkersOnSite !== false);
    } catch (error) {
      console.error('Failed to fetch organisation:', error);
      toast.error(error.response?.data?.error || 'Failed to load organisation details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrganisation(); }, [fetchOrganisation]);

  const toggleTrackingCategory = (id) => {
    setTrackingCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const addCustomCategory = () => {
    const value = customCategoryInput.trim();
    if (!value || customCategories.includes(value)) return;
    setCustomCategories((prev) => [...prev, value]);
    setCustomCategoryInput('');
  };

  const removeCustomCategory = (value) => {
    setCustomCategories((prev) => prev.filter((c) => c !== value));
  };

  const handleSaveTracking = async () => {
    const selectedCount = Object.values(trackingCategories).filter(Boolean).length + customCategories.length;
    if (selectedCount === 0) {
      toast.error('Select at least one category to track');
      return;
    }
    try {
      setSavingTracking(true);
      const data = await organisationService.completeOnboarding({
        trackingCategories, customCategories, usesEiaCertificates, hasWorkersOnSite,
      });
      setOrganisation(data.organisation);
      // Keep AuthContext's copy (drives dashboard/sidebar filtering) in sync
      // without waiting for a full reload.
      if (authOrganisation) setAuthOrganisation(data.organisation);
      setEditingTracking(false);
      toast.success('Compliance tracking preferences saved successfully');
    } catch (error) {
      console.error('Failed to update compliance tracking preferences:', error);
      toast.error(error.response?.data?.error || 'Failed to save preferences');
    } finally {
      setSavingTracking(false);
    }
  };

  const handleCancelTracking = () => {
    setTrackingCategories(organisation?.trackingCategories || {});
    setCustomCategories(organisation?.customCategories || []);
    setCustomCategoryInput('');
    setUsesEiaCertificates(organisation?.usesEiaCertificates !== false);
    setHasWorkersOnSite(organisation?.hasWorkersOnSite !== false);
    setEditingTracking(false);
  };

  const handleSave = async () => {
    if (!nameInput.trim()) {
      toast.error('Organisation name cannot be empty');
      return;
    }
    try {
      setSaving(true);
      const data = await organisationService.updateMine({ name: nameInput.trim() });
      setOrganisation(data.organisation);
      setEditing(false);
      toast.success('Organisation updated successfully');
    } catch (error) {
      console.error('Failed to update organisation:', error);
      toast.error(error.response?.data?.error || 'Failed to update organisation');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setNameInput(organisation?.name || '');
    setEditing(false);
  };

  const roleInfo = user ? getRoleInfo(user.role, user.customRoleTitle) : null;

  return (
    <div className="monitoring-page settings-page">
      <div className="monitoring-header">
        <div>
          <h1><i className="fas fa-cog"></i> Settings</h1>
          <p className="header-subtitle">Your organisation and account details</p>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading settings...</h3>
        </div>
      ) : (
        <div className="settings-cards-grid">
          <div className="settings-card">
            <div className="settings-card-header">
              <h3><i className="fas fa-building"></i> Organisation</h3>
              {canEdit && !editing && (
                <button className="btn-icon" title="Edit" onClick={() => setEditing(true)}>
                  <i className="fas fa-edit"></i>
                </button>
              )}
            </div>

            <div className="settings-field">
              <label>Name</label>
              {editing ? (
                <div className="settings-edit-row">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    autoFocus
                  />
                  <button className="btn-primary-green" disabled={saving} onClick={handleSave}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn-cancel" onClick={handleCancel} disabled={saving}>Cancel</button>
                </div>
              ) : (
                <p className="settings-value">{organisation?.name || '—'}</p>
              )}
              {!canEdit && (
                <small className="form-hint">Only an organisation admin can edit this.</small>
              )}
            </div>

            <div className="settings-field">
              <label>Type</label>
              <p className="settings-value">{ORG_TYPE_LABEL[organisation?.type] || organisation?.type || '—'}</p>
            </div>

            <div className="settings-field">
              <label>Member Since</label>
              <p className="settings-value">{formatDate(organisation?.createdAt)}</p>
            </div>
          </div>

          <div className="settings-card">
            <div className="settings-card-header">
              <h3><i className="fas fa-user"></i> Your Profile</h3>
            </div>
            <div className="settings-field">
              <label>Name</label>
              <p className="settings-value">{user?.firstName} {user?.lastName}</p>
            </div>
            <div className="settings-field">
              <label>Email</label>
              <p className="settings-value">{user?.email}</p>
            </div>
            <div className="settings-field">
              <label>Role</label>
              <p className="settings-value">
                {roleInfo?.icon && <i className={`fas ${roleInfo.icon}`} style={{ color: roleInfo.color, marginRight: '0.4rem' }}></i>}
                {roleInfo?.label || user?.role}
              </p>
            </div>
            <div className="settings-field">
              <label>Access Level</label>
              <p className="settings-value" style={{ textTransform: 'capitalize' }}>{user?.accessLevel}</p>
            </div>
          </div>

          <div className="settings-card full-width">
            <div className="settings-card-header">
              <h3><i className="fas fa-compass"></i> Compliance Tracking</h3>
              {canEdit && !editingTracking && (
                <button className="btn-icon" title="Edit" onClick={() => setEditingTracking(true)}>
                  <i className="fas fa-edit"></i>
                </button>
              )}
            </div>

            {editingTracking ? (
              <>
                <div className="settings-field">
                  <label>Categories your organisation tracks</label>
                  <div className="onboarding-categories-grid">
                    {TRACKING_CATEGORIES.map((category) => (
                      <div
                        key={category.id}
                        className={`onboarding-category-card ${trackingCategories[category.id] ? 'selected' : ''}`}
                        onClick={() => toggleTrackingCategory(category.id)}
                      >
                        <div className="onboarding-category-checkbox">
                          <input
                            type="checkbox"
                            checked={!!trackingCategories[category.id]}
                            onChange={() => toggleTrackingCategory(category.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="checkmark"></span>
                        </div>
                        <div className="onboarding-category-icon" style={{ backgroundColor: `${category.color}15`, color: category.color }}>
                          <i className={`fas ${category.icon}`}></i>
                        </div>
                        <div className="onboarding-category-label">{category.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="onboarding-custom-categories">
                    <label>Don't see something you track? Add it</label>
                    <div className="onboarding-custom-input-row">
                      <input
                        type="text"
                        value={customCategoryInput}
                        onChange={(e) => setCustomCategoryInput(e.target.value)}
                        placeholder="e.g. Supply Chain Ethics, Data Privacy"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addCustomCategory();
                          }
                        }}
                      />
                      <button type="button" className="btn-secondary-outline" onClick={addCustomCategory}>
                        <i className="fas fa-plus"></i> Add
                      </button>
                    </div>
                    {customCategories.length > 0 && (
                      <div className="onboarding-custom-tags">
                        {customCategories.map((category) => (
                          <span key={category} className="onboarding-custom-tag">
                            <i className="fas fa-tag"></i> {category}
                            <button type="button" onClick={() => removeCustomCategory(category)}>
                              <i className="fas fa-times"></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="settings-field">
                  <label>Formal EIA certificate to track?</label>
                  <div className="onboarding-yes-no">
                    <button
                      type="button"
                      className={`onboarding-choice ${usesEiaCertificates ? 'selected' : ''}`}
                      onClick={() => setUsesEiaCertificates(true)}
                    >
                      <i className="fas fa-file-contract"></i>
                      <div><strong>Yes</strong><p>Show EIA Certificates & Conditions</p></div>
                    </button>
                    <button
                      type="button"
                      className={`onboarding-choice ${!usesEiaCertificates ? 'selected' : ''}`}
                      onClick={() => setUsesEiaCertificates(false)}
                    >
                      <i className="fas fa-clipboard-check"></i>
                      <div><strong>No</strong><p>Hide EIA shortcuts for now</p></div>
                    </button>
                  </div>
                </div>

                <div className="settings-field">
                  <label>Workers on site?</label>
                  <div className="onboarding-yes-no">
                    <button
                      type="button"
                      className={`onboarding-choice ${hasWorkersOnSite ? 'selected' : ''}`}
                      onClick={() => setHasWorkersOnSite(true)}
                    >
                      <i className="fas fa-hard-hat"></i>
                      <div><strong>Yes</strong><p>Track worker counts per project</p></div>
                    </button>
                    <button
                      type="button"
                      className={`onboarding-choice ${!hasWorkersOnSite ? 'selected' : ''}`}
                      onClick={() => setHasWorkersOnSite(false)}
                    >
                      <i className="fas fa-clipboard-check"></i>
                      <div><strong>No</strong><p>Hide worker counts - not applicable to us</p></div>
                    </button>
                  </div>
                </div>

                <div className="settings-edit-row">
                  <button className="btn-primary-green" disabled={savingTracking} onClick={handleSaveTracking}>
                    {savingTracking ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn-cancel" onClick={handleCancelTracking} disabled={savingTracking}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="settings-field">
                  <label>Categories Tracked</label>
                  <p className="settings-value">
                    {organisation?.trackingCategories
                      ? [
                          ...TRACKING_CATEGORIES.filter((c) => organisation.trackingCategories[c.id]).map((c) => c.label),
                          ...(organisation.customCategories || []),
                        ].join(', ') || 'None selected'
                      : 'Not set up yet - showing everything by default'}
                  </p>
                </div>
                <div className="settings-field">
                  <label>Formal EIA Certificate</label>
                  <p className="settings-value">{organisation?.usesEiaCertificates === false ? 'No' : 'Yes'}</p>
                </div>
                <div className="settings-field">
                  <label>Workers on Site</label>
                  <p className="settings-value">{organisation?.hasWorkersOnSite === false ? 'No' : 'Yes'}</p>
                </div>
                {!canEdit && (
                  <small className="form-hint">Only an organisation admin can edit this.</small>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
